import React, {
  ComponentProps,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { SubmitErrorHandler, SubmitHandler } from 'react-hook-form'
import { ApolloError } from '@apollo/client'
import classNames from 'classnames'
import get from 'lodash/get'
import times from 'lodash/times'
import CloseIcon from 'transfix-icons/build/CloseIcon'
import { Button } from 'transfix-ui/components/Button'
import { IconButton } from 'transfix-ui/components/IconButton'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Pagination } from 'transfix-ui/components/Pagination'
import { Select, ISelectProps } from 'transfix-ui/components/Select'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import {
  ClickAwayListener,
  ClickAwayListenerProps,
  createStyles,
  makeStyles,
  Typography,
} from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import { brandColors } from 'transfix-ui/theme/colors'
import { useDebouncedCallback } from 'use-debounce/lib'
import { QueryParamConfigMap, StringParam } from 'use-query-params'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useQueryParams } from 'utils/hooks'
import { useScrollLock } from 'utils/useScrollLock'
import { ISavedSearch } from '../SavedSearches/types'
import { isDateRangeChildrenElement } from '../utils/isDateRangeChildrenElement'
import { ISummaryProps, Summary } from './Summary'
import { cleanObject } from './getCleanObject'

const NUM_SKELETON_ROWS = 16

export interface IFiltersProps<F> {
  defaultValues: Partial<F>
  onSubmitSuccess: SubmitHandler<F>
  onSubmitError?: SubmitErrorHandler<F>
  onClearAll: () => void
}

export type TSidebarSize = 'xs' | 's' | 'm' | 'l' | 'xl'

export interface TSortOption {
  label: string
  value: string
  sortByValue: string
  sortOrderValue: string
}

export interface IBasicListElement {
  id: string | number
}

export interface IHeaderProps<Filters> {
  filters: Partial<Filters>
  setValues: (props: Partial<Filters>) => void
}

export interface IRowProps<Element extends IBasicListElement> {
  element: Element
}

export type TFilters<Filters> = Filters & { sortBy?: string | null; sortOrder?: string | null }

/**
 * Component to render a PageList. It handles the query params and the pagination.
 */
export interface IPageListProps<ListElement extends IBasicListElement, Filters> {
  /**
   * The title of the page list
   * */
  title?: string
  /**
   * The Header component of the page list, this component is displayed above the list and receibes the following props:
   * - setValues: a function that sets the values of the filters
   * - props: the values of the selected filters
   */
  Header: (props: IHeaderProps<TFilters<Filters>>) => ReactElement | null
  /**
   * The Row component of the page list, this component is displayed in the list and receives the following props:
   * - element: the element of the list
   */
  Row: (props: IRowProps<ListElement>) => ReactElement | null
  /**
   * The Skeleton Row component of the page list, this component is displayed in the list while the data is loading
   */
  SkeletonRow?: () => ReactElement
  /**
   * The Sidebar component of the page list, this component is displayed in the right-side navigation and receives the filters props
   */
  SidebarComponent?: (props: IFiltersProps<TFilters<Filters>>) => ReactElement | null
  /**
   * it defines the default values of the filters and Filters type
   */
  defaultFilterValues: Partial<TFilters<Filters>>
  /**
   * it defines the data you want to save in the query params
   */
  configQueryConfig: QueryParamConfigMap
  /**
   * it defines the list of elements you want to display, also it defines the type of the elements
   */
  options: ListElement[]
  /**
   * the total number of elements
   */
  totalOptions: number
  /**
   * the number of elements per page
   */
  itemsPerPage: number
  /**
   * a boolean that defines if the filters side panel should be displayed
   * @default false
   */
  showFilters?: boolean
  /**
   * a function that hides the filters
   */
  onFilterHide?: () => void
  /**
   * a function that changes the filters
   */
  onFilterChange: (filters: Partial<TFilters<Filters>>) => void
  /**
   * a function that handles the error of the filters
   */
  onFilterError?: SubmitErrorHandler<Partial<TFilters<Filters>>>
  /**
   * Props used for for saving multiple searches/filter preferences in the Summary
   */
  SavedSearchesProps?: {
    savedSearches?: ISavedSearch[]
    onSaveSearch?: (
      filters: Partial<TFilters<Filters>>,
      filterName: string
    ) => boolean | Promise<boolean>
    onRemoveSavedSearch?: (id: string) => boolean | Promise<boolean>
    onSaveDefault?: (newDefault: ISavedSearch) => void
  }
  /**
   * a function that returns a readable string of the filters
   */
  filtersSummary?: (filters: Partial<TFilters<Filters>>) => string
  /**
   * a function that changes the page
   */
  onPageChange?: (
    isNext: boolean,
    filters: Partial<TFilters<Filters>>,
    pageNumber: number
  ) => Promise<void> | void
  /**
   * a boolean that defines if the list is loading
   */
  loading: boolean
  /**
   * error returned from graphql query
   */
  error?: ApolloError | undefined
  /**
   * The table header component of the page list, this component is displayed above the list and receives
   */
  TableHeader?: () => ReactElement | null
  /**
   * show the title
   * @default true
   */
  showTitle?: boolean
  /**
   * Sort options
   */
  sortOptions?: TSortOption[]
  /**
   * @optional Summary component that replace the generic/default Summary shown above the table header
   */
  Summary?: (props: ISummaryProps<Filters>) => ReactElement | null
  /**
   * Show numbers on pagination
   * @default false
   */
  displayPageNumbers?: boolean
  /**
   * @optional Component that appears when showEmptyState is true. This is shown in lieu of table rows
   */
  EmptyStateComponent?: () => ReactElement
  /**
   * Show a message to user instead of rows
   * @optional
   * @default false
   */
  showEmptyState?: boolean
  /**
   * Set the size of the side bar
   * @optional
   * @default 's'
   */
  sidebarSize?: TSidebarSize
  /**
   * Set the title of the side bar
   * @optional
   * @default Filters
   */
  sidebarTitle?: string
  /**
   * Actions for the page list (those will be positioned on the right side of the title)
   * @optional
   * @default []
   */
  actions?: {
    ButtonProps: ComponentProps<typeof Button>
    id: string
  }[]
  /**
   * When showing the total number of elements, it would show the range that the list it's showing
   * @default false
   */
  showCurrentElements?: boolean
}

const SIDERBAR_SIZE_MAPPER: Record<TSidebarSize, number> = {
  xs: 320,
  s: 400,
  m: 480,
  l: 560,
  xl: 640,
}

const usePageListStyles = makeStyles(theme =>
  createStyles<string, { sidebarSize: TSidebarSize }>({
    filtersContainer: ({ sidebarSize }) => ({
      position: 'absolute',
      top: 0,
      right: 0,
      width: '100%',
      height: '100%',
      zIndex: 2,
      backgroundColor: brandColors.white,
      animation: '$slidein 0.3s ease-in-out',
      [theme.breakpoints.up('md')]: {
        top: 48,
        width: SIDERBAR_SIZE_MAPPER[sidebarSize],
      },
    }),
    title: {
      padding: theme.spacing(3, 3, 1, 3),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    filters: {
      overflowY: 'scroll',
      height: 'calc(100vh - 118px)',
    },
    pagination: {
      textAlign: 'center',
      padding: theme.spacing(3, 0),
    },
    titleContainer: {
      padding: theme.spacing(1.5, 0),
      display: 'flex',
      justifyContent: 'space-between',
    },
    actionsContainer: {
      gap: theme.spacing(1),
      display: 'flex',
    },
    spacedRow: {
      padding: theme.spacing(1.5, 0),
      display: 'grid',
    },
    summaryWithSortOptions: {
      gridTemplateColumns: 'auto 200px',
    },
    container: {
      padding: theme.spacing(0, 3),
    },
    '@keyframes slidein': {
      '0%': {
        right: -320,
      },
      '100%': {
        right: 0,
      },
    },
  })
)

function PageList<ListElement extends IBasicListElement, Filters>({
  SidebarComponent,
  Header,
  Row,
  SkeletonRow = () => <Skeleton height={63} />,
  actions = [],
  onFilterHide,
  options,
  totalOptions,
  itemsPerPage,
  showFilters = false,
  defaultFilterValues,
  configQueryConfig,
  title,
  onFilterChange,
  onFilterError,
  filtersSummary,
  onPageChange,
  loading,
  error,
  showTitle = true,
  sidebarSize = 's',
  sidebarTitle = '',
  sortOptions,
  TableHeader = () => null,
  displayPageNumbers = false,
  Summary: SummaryProp,
  EmptyStateComponent,
  showEmptyState = false,
  showCurrentElements = false,
  SavedSearchesProps,
}: IPageListProps<ListElement, Filters>) {
  const enableMultiSaveSearches = useFeatureFlag('mpIssuePageListSaveSearches')

  const styles = usePageListStyles({ sidebarSize })
  const [page, setPage] = useState<number>(1)
  const { onRemoveSavedSearch, onSaveDefault, onSaveSearch, savedSearches } =
    SavedSearchesProps || {}

  /**
   * this is to show the filters component, it should show as a modal, but aligned to the right
   * So this hook is in charge to put the gray background behind the filters and block the pointer events
   */
  useScrollLock(showFilters, '.modal-container.active', 'modal-open')

  const [queryParams, setQueryParams] = useQueryParams({
    ...configQueryConfig,
    sortBy: StringParam,
    sortOrder: StringParam,
  })
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const setFilters = useCallback(
    (filters: Partial<Filters | { sortBy?: string; sortOrder?: string }>) => {
      setQueryParams(filters)
    },
    [setQueryParams]
  )

  const filters: Partial<TFilters<Filters>> = useMemo(
    () => ({
      ...cleanObject(defaultFilterValues),
      ...cleanObject(queryParams),
    }),
    [defaultFilterValues, queryParams]
  )

  const setValues = useCallback(
    (values: Partial<TFilters<Filters>>) => {
      const result = {
        ...filters,
        ...cleanObject(values),
      }
      setFilters(result)
      setPage(1)
    },
    [filters, setFilters]
  )

  const clearHandler = useCallback(() => {
    const resetValues = Object.keys(queryParams).reduce((acc, key) => ({ ...acc, [key]: null }), {})

    setFilters(resetValues)
    setPage(1)
    if (onFilterHide) onFilterHide()
  }, [onFilterHide, queryParams, setFilters])

  const submitFiltersHandler: SubmitHandler<Partial<TFilters<Filters>>> = useCallback(
    values => {
      setValues({
        ...filters,
        ...cleanObject(values),
      })
      if (onFilterHide) onFilterHide()
    },
    [filters, onFilterHide, setValues]
  )

  const filterContainerClasses = classNames('modal-container', {
    active: showFilters,
  })

  const pageChangeHandler = useCallback(
    (newPage: number) => {
      if (onPageChange) onPageChange(newPage > page, filters, newPage)
      setPage(newPage)
    },
    [filters, onPageChange, setPage, page]
  )

  const sortByValue = sortOptions?.find(
    option =>
      option.sortByValue === get(filters, 'sortBy', '') &&
      option.sortOrderValue === get(filters, 'sortOrder', '')
  )

  const [sortBy, setSortBy] = useState<TSortOption | undefined>(sortByValue)

  /* When sortByValue is changed based on filters value (and not by manually setting the sort value using the dropdown), set the value state. */
  useEffect(() => {
    setSortBy(sortByValue)
  }, [sortByValue])

  const handleSaveSearch = useCallback(
    async (filters, filterName) => {
      if (!onSaveSearch) return
      if (enableMultiSaveSearches) onSaveSearch(filters, filterName)
      else {
        const result = await onSaveSearch(filters, filterName)
        if (result) {
          const resetValues = Object.keys(queryParams).reduce(
            (acc, key) => ({ ...acc, [key]: undefined }),
            {}
          )

          setFilters(resetValues)
          setPage(1)
          if (onFilterHide) onFilterHide()

          openToast({
            toastType: 'default',
            toastMessage: 'Search saved',
          })
        } else {
          openToast({
            toastType: 'alert',
            toastMessage: 'Error saving search',
          })
        }
      }
    },
    [enableMultiSaveSearches, onFilterHide, onSaveSearch, openToast, queryParams, setFilters]
  )

  const removeSavedSearchHandler = useCallback(
    (id: string) => {
      if (!onRemoveSavedSearch) return
      onRemoveSavedSearch(id)
    },
    [onRemoveSavedSearch]
  )

  useEffect(() => {
    onFilterChange(filters)
  }, [filters])

  const [changeSortDebounced] = useDebouncedCallback(() => {
    if (!sortBy) return

    setValues({
      ...filters,
      sortBy: sortBy.sortByValue,
      sortOrder: sortBy.sortOrderValue,
    })
  }, 100)

  const changeSortHandler: ISelectProps['onChange'] = e => {
    const { target } = e
    const { value } = target

    const sortOption = sortOptions?.find(option => option.value === value)

    if (!sortOption) return

    setSortBy(sortOption)

    changeSortDebounced()
  }

  const SummaryComponent = SummaryProp || Summary

  const numberOfPages = Math.ceil(totalOptions / itemsPerPage)

  const clickAwayHandler: ClickAwayListenerProps['onClickAway'] = useCallback(
    event => {
      if (!event?.target) return

      const isDateRangeElement = isDateRangeChildrenElement(event.target as Element)

      /**
       * Given that DateRange component uses a portal to render
       * we need to check that is not that element to close the panel
       */
      if (!isDateRangeElement && onFilterHide) {
        onFilterHide()
      }
    },
    [onFilterHide]
  )

  return (
    <div className={styles.container}>
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      {showTitle && (
        <div className={styles.titleContainer}>
          <Typography variant='h2'>{title}</Typography>
          {actions?.length > 0 ? (
            <div className={styles.actionsContainer}>
              {actions.map(({ ButtonProps, id }) => (
                <div key={id}>
                  <Button {...ButtonProps} />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}
      <div>
        <Header filters={filters} setValues={setValues} />
      </div>
      <div
        className={classNames(styles.spacedRow, {
          [styles.summaryWithSortOptions]: !!sortOptions,
        })}>
        <SummaryComponent
          filters={filters}
          title={title || ''}
          totalOptions={totalOptions}
          filtersSummary={filtersSummary}
          onSaveSearch={onSaveSearch ? handleSaveSearch : undefined}
          pageNumber={page}
          showCurrentElements={showCurrentElements}
          itemsPerPage={itemsPerPage}
          savedSearches={savedSearches}
          onRemoveSearch={removeSavedSearchHandler}
          disableSavedSearch={!SavedSearchesProps}
          onSaveDefault={onSaveDefault}
        />
        {sortOptions && (
          <Select options={sortOptions} value={sortBy?.value} onChange={changeSortHandler} />
        )}
      </div>
      <div>
        <TableHeader />
        {error && !loading && (
          <div>
            <InlineMessage
              type={INLINE_MESSAGE_VARIANTS.ALERT}
              message='Sorry, there was an issue loading results. Please try refreshing your page or adjusting selecting filters.'
            />
          </div>
        )}
        {loading && !error ? (
          times(itemsPerPage || NUM_SKELETON_ROWS)
            .map((_, i) => i)
            .map(key => <SkeletonRow key={key} />)
        ) : (
          <div>
            {showEmptyState && EmptyStateComponent && <EmptyStateComponent />}
            {!showEmptyState && options.map(option => <Row element={option} key={option.id} />)}
          </div>
        )}
      </div>

      {numberOfPages > 1 && onPageChange && (
        <div className={styles.pagination}>
          <Pagination
            onPageChange={pageChangeHandler}
            currentPage={page}
            numberOfPages={Math.ceil(totalOptions / itemsPerPage)}
            displayPageNumbers={displayPageNumbers}
            loading={loading}
          />
        </div>
      )}
      <div className={filterContainerClasses}>
        {showFilters && SidebarComponent && (
          <ClickAwayListener onClickAway={clickAwayHandler}>
            <div className={styles.filtersContainer}>
              <div className={styles.title}>
                <Typography variant='h3'>{sidebarTitle}</Typography>
                <IconButton color='tertiary' Icon={CloseIcon} size='large' onClick={onFilterHide} />
              </div>
              <div className={styles.filters}>
                <SidebarComponent
                  defaultValues={filters}
                  onSubmitSuccess={submitFiltersHandler}
                  onSubmitError={onFilterError}
                  onClearAll={clearHandler}
                />
              </div>
            </div>
          </ClickAwayListener>
        )}
      </div>
    </div>
  )
}

export default PageList
